import { useCallback, useEffect, useMemo } from 'react';

import isEqual from 'lodash/isEqual';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import { countOccurrence } from '@/pages/Catalog/CatalogTable/TableFilters/utils/countOccurrence';
import type { CountedLabelFunction } from '@/pages/Catalog/constants';
import {
  alphabeticallySortedFilters,
  energyTransitionModules,
  labeledFilterColumns,
  oilOnlyModules
} from '@/pages/Catalog/constants';
import { useFilter } from '@/pages/Catalog/contexts/filter/useFilter';
import type { CatalogItem } from '@/types/catalog';

import type { Row, Table } from '@tanstack/react-table';

type FilterOption = { label: string; count: number };

const useTableFilters = (table: Table<CatalogItem>) => {
  const { filters } = useFilter();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  useEffect(() => {
    const { only, [SHOW_SELECTED_URL_PARAM]: showSelectedSymbols, ...rest } = filters;
    syncSelectedSymbolsFilter(table, showSelectedSymbols, symbols);
    syncColumnFilters(table, rest, only);
  }, [filters, symbols]);

  const filteredRows: Array<Row<CatalogItem>> = table.getFilteredRowModel().rows;

  const getCountedLabels: CountedLabelFunction = useCallback(
    (key: string, joined?: boolean): FilterOption[] => {
      const counts: { [label: string]: number } = {};
      for (const row of filteredRows) {
        if (joined) {
          const label: string | undefined = row.getValue(key);
          const labels: string[] | undefined = label?.split(',');
          for (const l of labels ?? []) {
            countOccurrence(counts, l);
          }
        } else {
          const label: string | undefined = row.getValue(key);
          if (label) countOccurrence(counts, label);
        }
      }

      return Object.entries(counts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => {
          if (alphabeticallySortedFilters.includes(key)) {
            return a.label.localeCompare(b.label);
          }
          return b.count - a.count;
        });
    },
    [filteredRows]
  );

  const columnFiltersWithCounts = useMemo(
    () =>
      Object.entries(labeledFilterColumns).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value(getCountedLabels)
        }),
        {} as Record<string, FilterOption[]>
      ),
    [getCountedLabels]
  );

  return {
    columnFiltersWithCounts
  };
};

export default useTableFilters;

const syncSelectedSymbolsFilter = (
  table: Table<CatalogItem>,
  showSelectedSymbols: boolean | undefined,
  symbols: unknown[]
) => {
  const codeColumn = table.getColumn('Code');
  if (!codeColumn) return;

  const current = (codeColumn.getFilterValue() as unknown[] | undefined) ?? [];
  const desired = showSelectedSymbols ? symbols : [];
  if (!isEqual(current, desired)) {
    codeColumn.setFilterValue(desired);
  }
};

const syncColumnFilters = (
  table: Table<CatalogItem>,
  filters: Record<string, unknown>,
  only: string | null | undefined
) => {
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'search') continue; // handled via table.setGlobalFilter in TableSearch
    if (key === 'IndexModule' && only && value === null) {
      applyOnlyIndexModuleFilter(table, only);
      continue;
    }

    const current = table.getColumn(key)?.getFilterValue() ?? [];
    const next = value ? [value] : [];
    if (!isEqual(current, next)) {
      table.getColumn(key)?.setFilterValue(next);
    }
  }
};

const applyOnlyIndexModuleFilter = (table: Table<CatalogItem>, only: string) => {
  if (only === 'oil') {
    table.getColumn('IndexModule')?.setFilterValue(oilOnlyModules);
    return;
  }
  if (only === 'energyTransition') {
    table.getColumn('IndexModule')?.setFilterValue(energyTransitionModules);
  }
};
